import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { TASK_IDS } from "@/shared/constants/taskIds";
import { filterAnswersForTask } from "@/shared/utils/filterAnswersForTask";
import type { FlowAnswers } from "@/shared/constants/flowTypes";

const ANSWER_TASK_MAP: Record<string, string> = {
  will_location: TASK_IDS.PROBATE_CHECK_WILL,
  will_condition_issues: TASK_IDS.PROBATE_CHECK_WILL,
  will_condition_warning: TASK_IDS.PROBATE_CHECK_WILL,
  will_copy_info: TASK_IDS.PROBATE_CHECK_WILL,
  will_safe_deposit_redirect: TASK_IDS.PROBATE_CHECK_WILL,
  executor_status: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  successor_executor: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  executor_all_decline_info: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  self_proving_affidavit: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  self_proving_confirmed: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  witness_status: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  witness_statement_info: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  witness_unavailable_info: TASK_IDS.PROBATE_EXECUTOR_WITNESSES,
  family_triage_intro: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_marriages: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_children: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_extended_family: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_review: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_loading: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_reveal: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_married_at_death: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_spouse_first_name: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_spouse_middle_name: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_spouse_last_name: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_spouse_status: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_spouse_death_year: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_earlier_marriages: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_earlier_marriages_list: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_has_children: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_children_list: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_has_living_parents: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_parents_list: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_has_siblings: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_triage_siblings_list: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  familyTreeConfirmed: TASK_IDS.PROBATE_FAMILY_TRIAGE,
  family_signoff_loop: TASK_IDS.PROBATE_FAMILY_SIGNOFFS,
  family_signoff_consents: TASK_IDS.PROBATE_FAMILY_SIGNOFFS,
};

interface ContactUpdate {
  id: number;
  sign_off_status: string;
}

interface SaveProbateStepArgs {
  estateId: number;
  taskId: string;
  answers: Record<string, unknown>;
  status: "in_progress" | "completed";
  contacts?: ContactUpdate[];
}

interface TaskRow {
  task_id: string;
  status: string;
}

interface CachedTaskRow {
  task_id: string;
  answers: Record<string, unknown> | null;
  status: string;
}

export function useProbateStepSave() {
  const { person } = useAppContext();
  const queryClient = useQueryClient();
  const estateId = person?.estate_id;
  const pendingSaveRef = useRef<Promise<unknown>>(Promise.resolve());

  const getTaskStatus = useCallback(
    (taskId: string): string | undefined => {
      const cached = queryClient.getQueryData<TaskRow[]>(["estateTasks", estateId, "probate"]);
      return cached?.find((r) => r.task_id === taskId)?.status;
    },
    [queryClient, estateId],
  );

  const mutation = useMutation({
    mutationFn: async ({ estateId: eid, taskId, answers, status, contacts }: SaveProbateStepArgs) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const { data, error } = await supabase.functions.invoke("legal/probate/save-step", {
        body: {
          estateId: eid,
          taskId,
          answers,
          status,
          ...(contacts ? { contacts } : {}),
          ...(answers.familyTreeConfirmed === "true" ? { familyTreeConfirmed: true } : {}),
        },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });

      if (error) throw new Error(error.message);
      return data;
    },
    onMutate: async (vars) => {
      const queryKey = ["estateTasks", vars.estateId, "probate"] as const;
      await queryClient.cancelQueries({ queryKey: [...queryKey] });
      const previous = queryClient.getQueryData<CachedTaskRow[]>([...queryKey]);
      if (previous) {
        queryClient.setQueryData<CachedTaskRow[]>([...queryKey],
          previous.map((row) =>
            row.task_id === vars.taskId
              ? { ...row, status: vars.status, answers: { ...(row.answers ?? {}), ...vars.answers } }
              : row,
          ),
        );
      }
      return { previous, queryKey: [...queryKey] };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["estateTasks", estateId] });
      queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
    },
  });

  const saveStep = useCallback(
    async (
      screenId: string,
      _questionId: string | undefined,
      _answer: string | string[] | undefined,
      allAnswers: FlowAnswers,
    ) => {
      if (!estateId) return;

      const taskId = ANSWER_TASK_MAP[screenId];
      if (!taskId) return;

      const taskAnswers = filterAnswersForTask(allAnswers, taskId, ANSWER_TASK_MAP);

      const currentStatus = getTaskStatus(taskId);
      const status = currentStatus === "completed" ? "completed" : "in_progress";
      const promise = mutation.mutateAsync({ estateId, taskId, answers: taskAnswers, status });
      pendingSaveRef.current = pendingSaveRef.current.catch(() => {}).then(() => promise);
      await promise;
    },
    [estateId, mutation, getTaskStatus],
  );

  const completeTask = useCallback(
    async (taskId: string, allAnswers: FlowAnswers, contacts?: ContactUpdate[]) => {
      if (!estateId) return;

      await pendingSaveRef.current;

      const taskAnswers = filterAnswersForTask(allAnswers, taskId, ANSWER_TASK_MAP);

      await mutation.mutateAsync({ estateId, taskId, answers: taskAnswers, status: "completed", contacts });
    },
    [estateId, mutation],
  );

  const resetPendingSave = useCallback(() => {
    pendingSaveRef.current = Promise.resolve();
  }, []);

  return { saveStep, completeTask, resetPendingSave, screenTaskMap: ANSWER_TASK_MAP };
}
